import { useMemo } from 'react'

/**
 * Pricing constants for loyalty home-to-airport transfers.
 * Values are in AUD and scoped to AU; regional expansion would require
 * making these configurable (e.g. by locale or backend).
 */
export const MAX_DISTANCE_KM = 80
export const FREE_DISTANCE_KM = 30
export const EXTRA_DISTANCE_PRICE_PER_KM = 3
export const CAR_SEAT_FEE = 30
export const VEHICLE_UPGRADE_FEE = 30
export const MAX_PASSENGERS_PER_BASE_VEHICLE = 3
export const MAX_BAGS_FOR_BASE_VEHICLE = 2

interface TransferPricingInput {
  kmFromAirport: number | undefined
  travellers: App.ExperienceTransferTravellers
  baggage: App.OrderTransferItemBaggage
}

interface TransferPricingResult {
  distanceExceedsMax: boolean
  hasExtraDistanceCharge: boolean
  extraDistanceKm: number
  extraDistanceCharge: number
  hasCarSeatCharge: boolean
  carSeatCharge: number
  needsVehicleUpgrade: boolean
  vehicleUpgradeCharge: number
  totalExtraCharge: number
}

/**
 * Memoized hook for calculating transfer pricing surcharges.
 * Calculates extra distance charges, car seat fees, and vehicle upgrade fees.
 */
function useTransferPricing(input: TransferPricingInput): TransferPricingResult {
  const { kmFromAirport, travellers, baggage } = input

  const distanceCharges = useMemo(() => {
    const distanceExceedsMax = kmFromAirport !== undefined && kmFromAirport > MAX_DISTANCE_KM
    const hasExtraDistanceCharge = kmFromAirport !== undefined &&
      kmFromAirport > FREE_DISTANCE_KM &&
      !distanceExceedsMax
    const extraDistanceKm = hasExtraDistanceCharge ? kmFromAirport - FREE_DISTANCE_KM : 0
    const extraDistanceCharge = hasExtraDistanceCharge ? extraDistanceKm * EXTRA_DISTANCE_PRICE_PER_KM : 0

    return { distanceExceedsMax, hasExtraDistanceCharge, extraDistanceKm, extraDistanceCharge }
  }, [kmFromAirport])

  const carSeatCharges = useMemo(() => {
    const totalCarSeats = (travellers.childSeats ?? 0) + (travellers.boosterSeats ?? 0)
    const hasCarSeatCharge = totalCarSeats > 0
    const carSeatCharge = hasCarSeatCharge ? CAR_SEAT_FEE : 0

    return { hasCarSeatCharge, carSeatCharge }
  }, [travellers.childSeats, travellers.boosterSeats])

  const vehicleUpgradeCharges = useMemo(() => {
    const totalPassengers = (travellers.adults ?? 0) + (travellers.children ?? 0)
    const totalBags = (baggage.bags ?? 0) + (baggage.oversizedBags ?? 0)
    const needsVehicleUpgrade = totalPassengers > MAX_PASSENGERS_PER_BASE_VEHICLE || totalBags > MAX_BAGS_FOR_BASE_VEHICLE
    const vehicleUpgradeCharge = needsVehicleUpgrade ? VEHICLE_UPGRADE_FEE : 0

    return { needsVehicleUpgrade, vehicleUpgradeCharge }
  }, [travellers.adults, travellers.children, baggage.bags, baggage.oversizedBags])

  const totalExtraCharge = useMemo(() => {
    return distanceCharges.extraDistanceCharge +
      carSeatCharges.carSeatCharge +
      vehicleUpgradeCharges.vehicleUpgradeCharge
  }, [distanceCharges.extraDistanceCharge, carSeatCharges.carSeatCharge, vehicleUpgradeCharges.vehicleUpgradeCharge])

  return {
    ...distanceCharges,
    ...carSeatCharges,
    ...vehicleUpgradeCharges,
    totalExtraCharge,
  }
}

export default useTransferPricing
